import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { EMPTY_BASELINE, verdictCellClass } from '../../utils/baselineComparison';
import {
  headingText, missingValueText, parseUnfamiliarProperties, provenanceNotes,
} from '../../utils/unfamiliarProperties';

// Unfamiliar Sign-in Properties panel, shared by the Alerts and Test Run Results pages
// (CLAUDE.md rule #13). Renders the pipeline-owned `unfamiliar_properties_json` (ALERT-FACTS-CONTRACT.md
// §10) on Entra ID Protection pass-through alerts: one row per property Microsoft flagged, with the
// sign-in's value and the user's 7/30-day baseline. Renders NOTHING when the alert carries no such
// property, which is the case for every other kind of alert.

function BaselineCell({ value }) {
  if (value == null || value === '' || value === EMPTY_BASELINE) {
    return <span className="text-gray-600 italic">{EMPTY_BASELINE}</span>;
  }
  return <span className="text-gray-300 font-mono break-words">{value}</span>;
}

/**
 * Props:
 *   json         the row's unfamiliarPropertiesJson (string or object)
 *   defaultOpen  expand on first render (default true: on these alerts it is the point of the alert)
 */
export default function UnfamiliarPropertiesPanel({ json, defaultOpen = true }) {
  const [collapsed, setCollapsed] = useState(!defaultOpen);
  const parsed = parseUnfamiliarProperties(json);
  if (!parsed) return null;

  const title = headingText(parsed);
  const hot = !parsed.unsupported && (parsed.counts.deviation > 0 || parsed.counts.partial > 0);

  return (
    <div>
      <div
        className="flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}
      >
        {collapsed
          ? <ChevronRightIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
          : <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
        <span className={`text-xs ${hot ? 'text-amber-400' : 'text-gray-400'} font-semibold uppercase tracking-wide`}>{title}</span>
      </div>
      {!collapsed && (
        parsed.unsupported ? (
          <p className="text-gray-500 italic mt-2 text-[10px]">
            This alert carries unfamiliar-property data in a format this portal version does not read
            (schema version {String(parsed.schemaVersion ?? 'unknown')}). Reload the portal; if this persists, the
            pipeline changed the format.
          </p>
        ) : parsed.properties.length === 0 ? (
          <p className="text-gray-600 italic mt-2 text-[10px]">Microsoft named no specific properties on this alert.</p>
        ) : (
          <div className="mt-2 text-[10px]">
            <div className="grid grid-cols-4 gap-x-6 text-gray-400 uppercase tracking-wide text-[10px] mb-1.5 pb-1 border-b border-gray-800/40">
              <span>Property</span>
              <span>Sign-in value</span>
              <span>7-day baseline</span>
              <span>30-day baseline</span>
            </div>
            {parsed.properties.map((p) => (
              <div key={p.key} className="grid grid-cols-4 gap-x-6 py-0.5 border-b border-gray-800/20 last:border-0">
                <span className="text-gray-400" title={p.reason || undefined}>{p.property}</span>
                <span className="min-w-0">
                  {p.recorded && p.value != null && p.value !== ''
                    ? <span className={`${verdictCellClass(p.verdict)} break-words`}>{p.value}</span>
                    : <span className="text-gray-500 italic">{missingValueText(parsed)}</span>}
                  {p.basisNote && (
                    <span className="block text-[9px] text-gray-500 normal-case mt-0.5">{p.basisNote}</span>
                  )}
                </span>
                <span><BaselineCell value={p.baseline7d} /></span>
                <span><BaselineCell value={p.baseline30d} /></span>
              </div>
            ))}
            {provenanceNotes(parsed).map((n) => (
              <p key={n} className="mt-1 text-[9px] text-gray-600">{n}</p>
            ))}
          </div>
        )
      )}
    </div>
  );
}
